/**
 * IndexedDB-based PDF caching utility
 * IndexedDB has much higher storage quota than localStorage/sessionStorage (typically 50MB+)
 */

const DB_NAME = 'PurchasingAgentDB';
const STORE_NAME = 'pdfs';
const DB_VERSION = 1;

interface CachedPDF {
  url: string;
  data: ArrayBuffer;
  timestamp: number;
}

let dbInstance: IDBDatabase | null = null;

const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'url' });
      }
    };
  });
};

export const cachePDF = async (url: string, data: ArrayBuffer): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const cacheEntry: CachedPDF = {
      url,
      data,
      timestamp: Date.now()
    };

    return new Promise((resolve, reject) => {
      const request = store.put(cacheEntry);
      request.onerror = () => reject(new Error('Failed to cache PDF'));
      request.onsuccess = () => {
        console.log(`PDF cached successfully: ${url}`);
        resolve();
      };
    });
  } catch (error) {
    console.warn('Failed to cache PDF in IndexedDB:', error);
    // Don't throw - caching failure shouldn't break the app
  }
};

export const getCachedPDF = async (url: string): Promise<ArrayBuffer | null> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get(url);
      request.onerror = () => reject(new Error('Failed to retrieve cached PDF'));
      request.onsuccess = () => {
        const result = request.result as CachedPDF | undefined;
        if (result) {
          console.log(`PDF loaded from IndexedDB cache: ${url}`);
          resolve(result.data);
        } else {
          resolve(null);
        }
      };
    });
  } catch (error) {
    console.warn('Failed to retrieve PDF from IndexedDB:', error);
    return null;
  }
};

export const clearPDFCache = async (url?: string): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = url ? store.delete(url) : store.clear();
      request.onerror = () => reject(new Error('Failed to clear PDF cache'));
      request.onsuccess = () => {
        console.log(`PDF cache cleared${url ? ` for ${url}` : ''}`);
        resolve();
      };
    });
  } catch (error) {
    console.warn('Failed to clear PDF cache:', error);
  }
};

export const getPDFCacheSize = async (): Promise<number> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onerror = () => reject(new Error('Failed to get cache size'));
      request.onsuccess = () => {
        const pdfs = request.result as CachedPDF[];
        const totalSize = pdfs.reduce((acc, pdf) => acc + pdf.data.byteLength, 0);
        resolve(totalSize);
      };
    });
  } catch (error) {
    console.warn('Failed to get PDF cache size:', error);
    return 0;
  }
};
